/* eslint-disable no-nested-ternary */
import { AgNumberValueCellEditingWithLoaderRenderer } from 'components/AgGrid/AgNumberValueCellEditingWithLoaderRenderer';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderOtherPriceData } from 'gql/graphql';
import { DeviationEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';
import { DeviatedCell } from 'utils/tableCells';

import type { ICellRendererParams } from 'ag-grid-community';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { updateActKC2OtherCostAcceptedCost, updateActKC2OtherCostPlanCost } from '../workOrderOtherTableQueries';

import Styles from './WorkOrderOtherEditableCell.module.css';

export const WorkOrderOtherActEditableCell = memo(
    (
        params: ICellRendererParams<WorkOrderOtherPriceData> & {
            deviationKey: string;
            isEditable: boolean;
            actKCId: number;
            decimal: number;
            handleUpdateValue: (data: WorkOrderOtherPriceData | undefined, actKCId: number, value: number) => void;
            isDisabled: boolean;
            currentColId: string;
            reloadDataHandler: () => void;
            valueType: 'plan' | 'accepted';
        },
    ) => {
        const dispatch = useDispatch();

        const {
            deviationKey,
            isEditable,
            actKCId,
            currentColId,
            decimal = 2,
            handleUpdateValue,
            isDisabled,
            value: d,
            getValue,
            column,
            data,
            reloadDataHandler,
            valueType,
            ...rest
        } = params;

        const updateActPlanCostId = useMemo(() => `updateActPlanCost${data?.id}`, [data?.id]);
        const updateActAcceptedCost = useMemo(() => `updateActAcceptedCost${data?.id}`, [data?.id]);

        const [updateActPlanCostReset] = useResetStoreData({
            requestId: updateActPlanCostId,
        });

        const [updateActAcceptedCostReset] = useResetStoreData({
            requestId: updateActAcceptedCost,
        });

        const onSuccess = useCallback(() => {
            if (valueType === 'plan') {
                updateActPlanCostReset(undefined);
            } else {
                updateActAcceptedCostReset(undefined);
            }

            reloadDataHandler();
        }, [reloadDataHandler, updateActAcceptedCostReset, updateActPlanCostReset, valueType]);

        const [, updateActPlanCostStatus, , updateActPlanCostRequest, , updateActPlanCostAbortRequest] = useRequest({
            requestId: updateActPlanCostId,
            query: updateActKC2OtherCostPlanCost,
            withEffect: false,
            dependenciesUpdater: onSuccess,
        });

        const [, updateActAcceptedCostStatus, , updateActAcceptedCostRequest, , updateActAcceptedCostAbortRequest] =
            useRequest({
                requestId: updateActAcceptedCost,
                query: updateActKC2OtherCostAcceptedCost,
                withEffect: false,
                dependenciesUpdater: onSuccess,
            });

        const value = useMemo(() => getValue?.().value || '', [getValue]);

        const isAct = useMemo(() => typeof data?.id === 'number', [data?.id]);

        const isShowLoader = useMemo(
            () =>
                valueType === 'plan'
                    ? updateActPlanCostStatus === RequestStatus.FETCHING
                    : updateActAcceptedCostStatus === RequestStatus.FETCHING,
            [updateActAcceptedCostStatus, updateActPlanCostStatus, valueType],
        );

        const onChangeHandler = useCallback(
            (newInputValue: number | null) => {
                if (valueType === 'plan') {
                    if (updateActPlanCostStatus === RequestStatus.FETCHING) {
                        dispatch(updateActPlanCostAbortRequest());
                    }

                    dispatch(
                        updateActPlanCostRequest({
                            actKC2Id: actKCId,
                            workOrderOtherId: data?.id,
                            cost: replacerCost(newInputValue ?? 0),
                        }),
                    );
                } else {
                    if (updateActAcceptedCostStatus === RequestStatus.FETCHING) {
                        dispatch(updateActAcceptedCostAbortRequest());
                    }

                    dispatch(
                        updateActAcceptedCostRequest({
                            actKC2Id: actKCId,
                            workOrderOtherId: data?.id,
                            cost: replacerCost(newInputValue ?? 0),
                        }),
                    );
                }
            },
            [
                actKCId,
                data?.id,
                dispatch,
                updateActAcceptedCostAbortRequest,
                updateActAcceptedCostRequest,
                updateActAcceptedCostStatus,
                updateActPlanCostAbortRequest,
                updateActPlanCostRequest,
                updateActPlanCostStatus,
                valueType,
            ],
        );

        return !isAct ? null : !isEditable ? (
            (data as any)[deviationKey] === DeviationEnum.Up ? (
                <DeviatedCell>{value}</DeviatedCell>
            ) : (
                value
            )
        ) : (
            <AgNumberValueCellEditingWithLoaderRenderer
                isEditable
                idKey="id"
                columnKey={currentColId}
                data={data}
                value={value}
                decimalScale={decimal}
                onChange={onChangeHandler}
                debounceTime={750}
                width="full"
                size="xs"
                disabled={!!(isDisabled || data?.isExcludedPrice)}
                leftSide={(data as any)[deviationKey] === DeviationEnum.Up ? '▲' : undefined}
                className={Styles.workOrderOtherEditableCell}
                isShowLoader={isShowLoader}
                {...rest}
            />
        );
    },
);

WorkOrderOtherActEditableCell.displayName = 'WorkOrderOtherActEditableCell';
